import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

const FONT = 'Plus Jakarta Sans, sans-serif';
const DEFAULT_COLOR = 'hsl(243 75% 59%)';

export interface EyebrowProps {
  /** Accent colour for background tint, border and text. */
  color?: string;
  /** Optional inline icon rendered before the text. */
  Icon?: LucideIcon;
  /** Extra classes appended to the base classes (e.g. margin overrides). */
  className?: string;
  children: ReactNode;
}

/**
 * Shared eyebrow pill used above page headings across the site.
 *
 * Single source of truth for the small pill/tag styling — indigo tint,
 * subtle border, tiny icon. Restyling every eyebrow site-wide is one
 * change here. Extracted from ServiceStoryPage; used by ServicePage,
 * FAQ hero, and any other page that needs a heading eyebrow.
 */
export default function Eyebrow({
  color = DEFAULT_COLOR,
  Icon,
  className = 'mb-3',
  children,
}: EyebrowProps) {
  return (
    <span
      className={`${className} inline-flex items-center gap-1.5 rounded-full px-3.5 py-1 text-xs font-semibold`}
      style={{
        background: `${color}15`,
        border: `1px solid ${color}30`,
        color,
        fontFamily: FONT,
      }}
    >
      {Icon && <Icon size={13} strokeWidth={2.25} />}
      {children}
    </span>
  );
}
